/**
 * De vraag die verschijnt zodra je een object door verschalen over andere
 * objecten heen trekt: **hier een groep van maken?**
 *
 * **Waarom dit bestaat.** Bij Sharp ontstaat een bedrijfsproces als conclusie,
 * niet als startpunt: je legt eerst de stappen in volgorde neer en trekt er
 * daarna een kader omheen. Het canvas kende die beweging niet — slepen nestte
 * wel, verschalen niet, dus een vergroot vakje legde zich als een deken over de
 * rij en er veranderde niets aan het model.
 *
 * **Waarom een keuze per regel en niet één voor de hele set.** Twee objecten in
 * dezelfde omtrek hoeven niet dezelfde relatietypen te ondersteunen — de matrix
 * bepaalt dat per paar. En zelfs als ze hetzelfde toestaan wil de modelleur er
 * per objecttype van kunnen afwijken. Eén venster met een rij per object dekt
 * allebei; de kop biedt "alles op ..." voor het gewone geval waarin ze wél
 * gelijk zijn.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useUi } from "../../state/ui";

const GEEN = "__geen__";

export function GroupEnclosePromptModal() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const prompt = useUi((s) => s.groupEnclosePrompt);
  const resolve = useUi((s) => s.resolveGroupEnclosePrompt);

  /** Keuze per object; `GEEN` = wel nesten, geen relatie. */
  const [keuze, setKeuze] = useState<Record<string, string>>({});

  // Bij het openen elk object op z'n eerste toegestane type zetten. Dat is de
  // meest voorkomende keuze en scheelt de modelleur werk; afwijken kan per rij.
  useEffect(() => {
    if (!prompt) return;
    const start: Record<string, string> = {};
    for (const item of prompt.items) {
      if (item.vastgelegd) continue;
      start[item.conceptId] = item.relationTypeIds[0] ?? GEEN;
    }
    setKeuze(start);
  }, [prompt]);

  useEffect(() => {
    if (!prompt) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        resolve(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prompt, resolve]);

  /**
   * De typen die élk object toestaat. Alleen als die niet leeg is heeft
   * "alles op ..." betekenis — bij een gemengde set is er geen gedeelde noemer
   * en blijft de keuze per rij staan.
   */
  const gedeeld = useMemo(() => {
    if (!prompt) return [] as string[];
    const teKiezen = prompt.items.filter((i) => !i.vastgelegd);
    if (teKiezen.length === 0) return [];
    let doorsnede: string[] = [...(teKiezen[0]?.relationTypeIds ?? [])];
    for (const item of teKiezen.slice(1)) {
      const set = new Set(item.relationTypeIds);
      doorsnede = doorsnede.filter((id) => set.has(id));
    }
    return doorsnede;
  }, [prompt]);

  if (!prompt) return null;

  const reg = getRegistry();
  const relatieLabel = (id: string) => {
    const rt = reg.getRelationType(id);
    return rt ? resolveBilingual(rt.label, lang) : id;
  };

  const bevestig = () => {
    const uit: Record<string, string | null> = {};
    for (const item of prompt.items) {
      if (item.vastgelegd) continue;
      const gekozen = keuze[item.conceptId];
      uit[item.conceptId] = !gekozen || gekozen === GEEN ? null : gekozen;
    }
    resolve(uit);
  };

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      onClick={() => resolve(null)}
      role="dialog"
      aria-modal="true"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: klik-buiten sluit, binnen niet */}
      <div
        className="w-full max-w-2xl rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-semibold text-base text-(--ld-text)">
          {t("groupEnclose.title", { count: prompt.items.length })}
        </h2>
        <p className="mt-1 text-(--ld-muted) text-sm">
          {t("groupEnclose.description", { group: prompt.groepNaam })}
        </p>

        {gedeeld.length > 0 && (
          <label className="mt-3 flex items-center gap-2 text-(--ld-text) text-sm">
            {t("groupEnclose.allTo")}
            <select
              className="h-7 rounded border border-(--ld-control) bg-(--ld-surface) px-2 text-sm"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setKeuze((prev) => {
                  const next = { ...prev };
                  for (const item of prompt.items) {
                    if (item.vastgelegd) continue;
                    if (v === GEEN || item.relationTypeIds.includes(v)) next[item.conceptId] = v;
                  }
                  return next;
                });
              }}
            >
              <option value="">{t("groupEnclose.choose")}</option>
              {gedeeld.map((id) => (
                <option key={id} value={id}>
                  {relatieLabel(id)}
                </option>
              ))}
              <option value={GEEN}>{t("groupEnclose.noRelation")}</option>
            </select>
          </label>
        )}

        <ul className="mt-3 max-h-72 overflow-y-auto">
          {prompt.items.map((item) => (
            <li
              key={item.conceptId}
              className="flex items-center justify-between gap-3 border-(--ld-rule) border-b py-1.5 last:border-b-0"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-(--ld-text) text-sm">{item.naam}</span>
                <span className="block truncate text-(--ld-muted) text-xs">{item.typeLabel}</span>
              </span>
              {item.vastgelegd ? (
                <span className="shrink-0 text-(--ld-muted) text-xs italic">{item.vastgelegd}</span>
              ) : item.relationTypeIds.length === 0 ? (
                <span className="shrink-0 text-(--ld-muted) text-xs italic">
                  {t("groupEnclose.nothingAllowed")}
                </span>
              ) : (
                <select
                  className="h-7 shrink-0 rounded border border-(--ld-control) bg-(--ld-surface) px-2 text-sm"
                  value={keuze[item.conceptId] ?? GEEN}
                  onChange={(e) =>
                    setKeuze((prev) => ({ ...prev, [item.conceptId]: e.target.value }))
                  }
                >
                  {item.relationTypeIds.map((id) => (
                    <option key={id} value={id}>
                      {relatieLabel(id)}
                    </option>
                  ))}
                  <option value={GEEN}>{t("groupEnclose.noRelation")}</option>
                </select>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={() => resolve(null)}
          >
            {t("groupEnclose.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-(--ld-primary) px-3 py-1.5 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover)"
            onClick={bevestig}
          >
            {t("groupEnclose.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
